import { useState, useCallback, useMemo, useEffect } from 'react'

/**
 * Custom hook to manage row visibility and a global "show all / hide all" state.
 *
 * @param {number} totalRows - Total number of rows to manage visibility for.
 * @returns {{
 *   showAll: boolean,
 *   toggleAll: () => void,
 *   toggleRow: (rowId: string) => void,
 *   isRowVisible: (rowId: string) => boolean
 * }}
 */
export const useMarginVisibility = (totalRows = 0) => {
  const [visibleRows, setVisibleRows] = useState({})

  // Generate an array of IDs as strings
  const rowIds = useMemo(
    () => Array.from({ length: totalRows }, (_, i) => String(i)),
    [totalRows]
  )

  // Compute derived states
  const allVisible = useMemo(() => {
    if (totalRows === 0) return false
    return rowIds.every(id => visibleRows[id])
  }, [visibleRows, rowIds, totalRows])

  const allHidden = useMemo(() => {
    if (totalRows === 0) return true
    return rowIds.every(id => !visibleRows[id])
  }, [visibleRows, rowIds, totalRows])

  // showAll is true if all are visible, false only if all are hidden
  // It keeps the last state if there is a mix
  const [showAll, setShowAll] = useState(false)

  // Synchronize showAll with the derived states
  useEffect(() => {
    if (allVisible) setShowAll(true)
    if (allHidden) setShowAll(false)
  }, [allVisible, allHidden])

  const toggleAll = useCallback(() => {
    setShowAll(prev => {
      const next = !prev

      if (next) {
        const allVisible = {}
        rowIds.forEach(id => {
          allVisible[id] = true
        })
        setVisibleRows(allVisible)
      } else {
        setVisibleRows({})
      }

      return next
    })
  }, [rowIds])

  const toggleRow = useCallback(rowId => {
    setVisibleRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }))
  }, [])

  const isRowVisible = useCallback(rowId => !!visibleRows[rowId], [visibleRows])

  return { showAll, toggleAll, toggleRow, isRowVisible }
}
